(() => {
  function initJplSolarSystem(root) {
    if (!root || root.dataset.jplssReady) return;
    root.dataset.jplssReady = "1";

    const $ = (selector) => root.querySelector(selector);
    const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[char]));
    const en = /^en/i.test(document.documentElement.lang || navigator.language || "");
    const dataUrl = root.dataset.jplUrl || "";
    const txt = en ? {
      eyebrow: "JPL Horizons",
      title: "Solar System",
      intro: "Interactive 3D view of the planets.",
      loading: "Loading solar system...",
      click: "Tap or click a planet for its description.",
      noJson: "No JSON file found; showing visual fallback positions.",
      jsonAge: "JSON age correction",
      today: "Today",
      days: "days",
      distance: "Current distance to Sun",
      avgDistance: "Average distance to Sun",
      speed: "Current speed",
      diameter: "Diameter",
      orbit: "Orbital period"
    } : {
      eyebrow: "JPL Horizons",
      title: "Zonnestelsel",
      intro: "Interactieve 3D-weergave van de planeten.",
      loading: "Zonnestelsel wordt geladen...",
      click: "Tik of klik op een planeet voor de beschrijving.",
      noJson: "Geen JSON-bestand gevonden; visuele fallback-posities worden getoond.",
      jsonAge: "JSON-tijdcorrectie",
      today: "Vandaag",
      days: "dagen",
      distance: "Huidige afstand tot zon",
      avgDistance: "Gemiddelde afstand tot zon",
      speed: "Huidige snelheid",
      diameter: "Diameter",
      orbit: "Omlooptijd"
    };

    root.querySelectorAll("[data-k]").forEach((node) => {
      if (txt[node.dataset.k]) node.textContent = txt[node.dataset.k];
    });
    const loadNode = $("[data-load]");
    if (loadNode) loadNode.textContent = txt.loading;
    const metaNode = $("[data-meta]");
    if (metaNode) metaNode.innerHTML = `<span>${esc(txt.click)}</span>`;

    const AU = en ? "AU" : "AE";
    const bodies = [
      ["mercury", "Mercurius", "Mercury", .387, 87.97, .055, 0xb7a99a, "Planeet", "4.879 km", "88 dagen"],
      ["venus", "Venus", "Venus", .723, 224.7, .085, 0xd6b070, "Planeet", "12.104 km", "225 dagen"],
      ["earth", "Aarde", "Earth", 1, 365.25, .095, 0x3d8cff, "Planeet", "12.742 km", "365,25 dagen"],
      ["mars", "Mars", "Mars", 1.524, 686.98, .075, 0xd36a4a, "Planeet", "6.779 km", "687 dagen"],
      ["jupiter", "Jupiter", "Jupiter", 5.203, 4332.6, .24, 0xd9b38c, "Gasreus", "139.820 km", "11,86 jaar"],
      ["saturn", "Saturnus", "Saturn", 9.537, 10759, .21, 0xd5c08c, "Gasreus", "116.460 km", "29,45 jaar"],
      ["uranus", "Uranus", "Uranus", 19.191, 30687, .15, 0x8bd8ff, "IJsreus", "50.724 km", "84 jaar"],
      ["neptune", "Neptunus", "Neptune", 30.07, 60190, .15, 0x5277ff, "IJsreus", "49.244 km", "164,8 jaar"]
    ];
    const bodyMap = Object.fromEntries(bodies.map((body) => [body[0], body]));
    const descriptions = {
      mercury: en ? "Mercury is the smallest planet and races closest to the Sun." : "Mercurius is de kleinste planeet en beweegt het dichtst langs de zon.",
      venus: en ? "Venus is a bright, hot world with a very dense atmosphere." : "Venus is een heldere, hete wereld met een zeer dichte atmosfeer.",
      earth: en ? "Earth is the rocky planet with oceans, weather and a protective atmosphere." : "Aarde is de rotsplaneet met oceanen, weer en een beschermende atmosfeer.",
      mars: en ? "Mars is a cold desert world with dust storms, volcanoes and polar ice." : "Mars is een koude woestijnwereld met stofstormen, vulkanen en poolijs.",
      jupiter: en ? "Jupiter is the largest planet, a gas giant with powerful storms." : "Jupiter is de grootste planeet, een gasreus met krachtige stormen.",
      saturn: en ? "Saturn is a gas giant known for its wide ring system." : "Saturnus is een gasreus die bekendstaat om zijn brede ringenstelsel.",
      uranus: en ? "Uranus is an ice giant that rotates on its side." : "Uranus is een ijsreus die als het ware op zijn kant roteert.",
      neptune: en ? "Neptune is a distant ice giant with very strong winds." : "Neptunus is een verre ijsreus met zeer krachtige winden."
    };

    let THREE;
    let OrbitControls;
    let renderer;
    let scene;
    let camera;
    let controls;
    let raycaster;
    let mouse;
    let starMaterial;
    let selectGlow;
    let lastData = null;
    let snapshotShift = 0;
    let activeKey = "";
    let raf = 0;
    let dead = false;
    const objects = {};
    const labels = {};
    const targets = [];

    function jd(date) {
      return date.getTime() / 864e5 + 2440587.5;
    }
    function daysFromJ2000(date) {
      return jd(date) - 2451545;
    }
    function fallbackVector(body, date) {
      const angle = (daysFromJ2000(date) / body[4] + body[3] * .137) * Math.PI * 2;
      const incline = (body[3] % .7) * .08;
      return {
        x: Math.cos(angle) * body[3],
        y: Math.sin(angle) * body[3],
        z: Math.sin(angle) * body[3] * incline,
        r_au: body[3]
      };
    }
    function snapshotAgeDays(data) {
      const raw = data && (data.target_date ? `${data.target_date}T00:00:00Z` : data.generated_at || data.time);
      const date = raw ? new Date(raw) : null;
      if (!date || !Number.isFinite(date.getTime())) return 0;
      return (Date.now() - date.getTime()) / 86400000;
    }
    function rotateVector(vector, key, days) {
      const body = bodyMap[key];
      if (!body || !vector) return vector;
      const angle = days / body[4] * Math.PI * 2;
      const c = Math.cos(angle);
      const s = Math.sin(angle);
      return {
        ...vector,
        x: vector.x * c - vector.y * s,
        y: vector.x * s + vector.y * c,
        z: vector.z
      };
    }
    function distance(vector) {
      return Math.hypot(+vector.x || 0, +vector.y || 0, +vector.z || 0) || 1;
    }
    function orbitRadius(au) {
      return Math.pow(au, .42) * 2.28 + .15;
    }
    function scenePosition(vector) {
      const d = distance(vector);
      const k = orbitRadius(d) / d;
      return new THREE.Vector3(vector.x * k, vector.z * k, -vector.y * k);
    }
    function fmt(value, digits = 2) {
      if (value == null || value === "") return "-";
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      return n.toLocaleString(en ? "en-GB" : "nl-NL", {maximumFractionDigits: digits});
    }

    async function fetchSnapshot() {
      const now = new Date();
      const fallback = {
        source_mode: "fallback",
        generated_at: now.toISOString(),
        bodies: Object.fromEntries(bodies.map((body) => [body[0], fallbackVector(body, now)]))
      };
      try {
        if (!dataUrl || /\.py(\?|$)/i.test(dataUrl)) return fallback;
        const raw = await fetch(dataUrl, {cache: "no-store"}).then((response) => response.ok ? response.json() : null);
        if (!raw || !raw.bodies) return fallback;
        const data = {...raw, source_mode: "jpl"};
        bodies.forEach((body) => {
          if (!data.bodies[body[0]]) data.bodies[body[0]] = fallbackVector(body, now);
        });
        return data;
      } catch (error) {
        return fallback;
      }
    }

    function renderInfo(key) {
      const body = bodyMap[key];
      const object = objects[key];
      const infoBox = $("[data-info]");
      if (!body || !object || !infoBox) return;
      const vector = object.userData.vector || {};
      const rows = [
        ["Type", body[7]],
        [txt.diameter, body[8]],
        [txt.avgDistance, `${body[3]} ${AU}`],
        [txt.orbit, body[9]],
        [txt.distance, `${fmt(distance(vector), 3)} ${AU}`],
        [txt.speed, vector.speed_km_s ? `${fmt(vector.speed_km_s, 2)} km/s` : "-"],
        [txt.jsonAge, snapshotShift ? `${fmt(snapshotShift, 1)} ${txt.days}` : txt.today]
      ];
      infoBox.classList.remove("on");
      infoBox.hidden = false;
      infoBox.innerHTML = `<div class="jsit">${esc(en ? body[2] : body[1])}</div><p>${esc(descriptions[key])}</p><dl>${rows.map((row) => `<div><dt>${esc(row[0])}</dt><dd>${esc(row[1])}</dd></div>`).join("")}</dl>`;
      requestAnimationFrame(() => infoBox.classList.add("on"));
    }

    function syncPlanets() {
      if (!lastData) return;
      bodies.forEach((body) => {
        const key = body[0];
        const base = lastData.bodies[key];
        const vector = rotateVector(base, key, snapshotShift);
        const object = objects[key];
        if (!object) return;
        object.position.copy(scenePosition(vector));
        object.userData.vector = vector;
      });
      if (activeKey) renderInfo(activeKey);
    }

    function createStars(count, size, opacity) {
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const radius = 26 + Math.random() * 70;
        positions[i * 3] = Math.sin(phi) * Math.cos(theta) * radius;
        positions[i * 3 + 1] = Math.cos(phi) * radius;
        positions[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * radius;
      }
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const material = new THREE.PointsMaterial({color: 0xffffff, size, transparent: true, opacity, depthWrite: false});
      scene.add(new THREE.Points(geometry, material));
      return material;
    }
    function createOrbit(radiusAu, color) {
      const geometry = new THREE.BufferGeometry();
      const points = [];
      for (let i = 0; i < 240; i++) {
        const vector = {
          x: Math.cos(i / 240 * Math.PI * 2) * radiusAu,
          y: Math.sin(i / 240 * Math.PI * 2) * radiusAu,
          z: 0
        };
        const point = scenePosition(vector);
        points.push(point.x, point.y, point.z);
      }
      geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
      scene.add(new THREE.LineLoop(geometry, new THREE.LineBasicMaterial({color, transparent: true, opacity: .18})));
    }
    function updateLabel(label, object) {
      const p = object.position.clone();
      p.y += .35;
      p.project(camera);
      const box = $("[data-scene]");
      label.style.transform = `translate(-50%,-50%) translate(${(p.x * .5 + .5) * box.clientWidth}px,${(-p.y * .5 + .5) * box.clientHeight}px)`;
      label.classList.toggle("on", p.z < 1);
    }

    async function initScene() {
      [THREE, {OrbitControls}] = await Promise.all([
        import("https://esm.sh/three@0.160.0"),
        import("https://esm.sh/three@0.160.0/examples/jsm/controls/OrbitControls.js")
      ]);
      const box = $("[data-scene]");
      const canvas = $("[data-c]");
      renderer = new THREE.WebGLRenderer({canvas, antialias: true, alpha: true});
      renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.04;
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(45, 1, .1, 160);
      camera.position.set(0, 7.2, 11);
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = .08;
      controls.rotateSpeed = 1.45;
      controls.zoomSpeed = .45;
      controls.panSpeed = .55;
      controls.minDistance = 5;
      controls.maxDistance = 28;
      scene.add(new THREE.AmbientLight(0x51637d, .55));
      scene.add(new THREE.PointLight(0xffddaa, 4, 80, 1.2));
      starMaterial = createStars(900, .035, .7);

      const sun = new THREE.Mesh(new THREE.SphereGeometry(.46, 48, 48), new THREE.MeshBasicMaterial({color: 0xffb23b}));
      sun.add(new THREE.Mesh(new THREE.SphereGeometry(.9, 48, 48), new THREE.MeshBasicMaterial({color: 0xff8a22, transparent: true, opacity: .18, blending: THREE.AdditiveBlending, depthWrite: false})));
      scene.add(sun);

      bodies.forEach((body) => createOrbit(body[3], body[6]));
      bodies.forEach((body) => {
        const material = new THREE.MeshStandardMaterial({color: body[6], roughness: .52, metalness: .05, emissive: body[6], emissiveIntensity: .035});
        const object = new THREE.Mesh(new THREE.SphereGeometry(body[5], 32, 32), material);
        object.userData.key = body[0];
        objects[body[0]] = object;
        targets.push(object);
        if (body[0] === "saturn") {
          const ring = new THREE.Mesh(new THREE.RingGeometry(body[5] * 1.45, body[5] * 2.1, 64), new THREE.MeshBasicMaterial({color: 0xd8c994, transparent: true, opacity: .46, side: THREE.DoubleSide}));
          ring.rotation.x = Math.PI / 2.6;
          object.add(ring);
        }
        scene.add(object);
        const label = document.createElement("div");
        label.className = "jslab";
        label.textContent = en ? body[2] : body[1];
        box.appendChild(label);
        labels[body[0]] = label;
      });

      selectGlow = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 32), new THREE.MeshBasicMaterial({color: 0x8bd8ff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.BackSide}));
      scene.add(selectGlow);
      raycaster = new THREE.Raycaster();
      mouse = new THREE.Vector2();

      let down = null;
      canvas.addEventListener("pointerdown", (event) => { down = {x: event.clientX, y: event.clientY}; });
      canvas.addEventListener("pointerup", (event) => {
        if (!down || Math.hypot(event.clientX - down.x, event.clientY - down.y) > 8) return;
        const rect = canvas.getBoundingClientRect();
        mouse.set((event.clientX - rect.left) / rect.width * 2 - 1, -((event.clientY - rect.top) / rect.height * 2 - 1));
        raycaster.setFromCamera(mouse, camera);
        const hit = raycaster.intersectObjects(targets, false)[0];
        if (!hit || !hit.object.userData.key) return;
        activeKey = hit.object.userData.key;
        const body = bodyMap[activeKey];
        selectGlow.scale.setScalar(body[5] * 2.7);
        renderInfo(activeKey);
      });

      const resize = () => {
        const width = box.clientWidth;
        const height = box.clientHeight;
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.fov = width < 560 ? 54 : 45;
        camera.updateProjectionMatrix();
      };
      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(box);
      addEventListener("resize", resize);
      document.addEventListener("shopify:section:unload", (event) => {
        if (!event.target || event.target.contains(root) || root.contains(event.target)) {
          dead = true;
          if (raf) cancelAnimationFrame(raf);
          resizeObserver.disconnect();
          removeEventListener("resize", resize);
          renderer.dispose();
        }
      });

      resize();
      lastData = await fetchSnapshot();
      snapshotShift = lastData.source_mode === "jpl" ? snapshotAgeDays(lastData) : 0;
      syncPlanets();
      if (loadNode) loadNode.classList.add("off");

      let last = performance.now();
      const loop = () => {
        if (dead) return;
        raf = requestAnimationFrame(loop);
        const now = performance.now();
        const dt = Math.min(.04, (now - last) / 1000);
        last = now;
        controls.update();
        sun.rotation.y += dt * .12;
        Object.values(objects).forEach((object) => { object.rotation.y += dt * .35; });
        starMaterial.opacity = .58 + Math.sin(now * .002) * .12;
        if (activeKey && objects[activeKey]) {
          selectGlow.position.copy(objects[activeKey].position);
          selectGlow.material.opacity = .15 + Math.sin(now * .006) * .04;
        } else {
          selectGlow.material.opacity = 0;
        }
        renderer.render(scene, camera);
        Object.entries(objects).forEach(([key, object]) => updateLabel(labels[key], object));
      };
      loop();
    }

    initScene().catch((error) => {
      console.error(error);
      if (loadNode) loadNode.textContent = en ? "The solar-system view could not be loaded." : "De zonnestelsel-weergave kon niet worden geladen.";
    });
  }

  function bootJplSolarSystems() {
    document.querySelectorAll("[data-jplss]").forEach(initJplSolarSystem);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootJplSolarSystems);
  } else {
    bootJplSolarSystems();
  }
  document.addEventListener("shopify:section:load", bootJplSolarSystems);
})();
